/*
 Copyright 2008 Cheezeworld.com

 Permission is hereby granted, free of charge, to any person
 obtaining a copy of this software and associated documentation
 files (the "Software"), to deal in the Software without
 restriction, including without limitation the rights to use,
 copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the
 Software is furnished to do so, subject to the following
 conditions:

 The above copyright notice and this permission notice shall be
 included in all copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
 OTHER DEALINGS IN THE SOFTWARE.
 */

import { Vector2D } from "./Vector2D";

import Stage = createjs.Stage;
import MouseEvent = createjs.MouseEvent;
import {KeyboardEvent} from "@goodgamestudios/createjs-utilities";

export class Input {

    private static _instance: Input;

    // last key pressed
    public lastKeyName: string = "";

    // mouse states
    public isMouseDown: boolean = false;
    public isMouseReleased: boolean = false;
    public mousePos: Vector2D;
    public mouseX: number = 0;
    public mouseY: number = 0;
    public mouseDelta: number = 0;

    private _stage: Stage;
    private _timeKeyPressed: number;
    private _timeKeyReleased: number;
    private _timeMousePressed: number;
    private _timeMouseReleased: number;
    private _nt: number;
    private _ot: number;
    private _dt: number;
    private _hasMouseScrolled: boolean;

    // Input vars
    private _ascii: any[];
    private _keyState: Map<string, number>;
    private _keyArr: any[];

    // Singleton
    public static get instance(): Input {
        if (Input._instance == null) {
            Input._instance = new Input();
        }
        return Input._instance;
    }

    public get timeSinceLastKeyPress(): number {
        return Date.now() - this._timeKeyPressed;
    }

    public get timeSinceLastKeyRelease(): number {
        return Date.now() - this._timeKeyReleased;
    }

    public get timeSinceMousePressed(): number {
        return Date.now() - this._timeMousePressed;
    }

    public get timeSinceMouseReleased(): number {
        return Date.now() - this._timeMouseReleased;
    }

    public get hasMouseScrolled(): boolean {
        if (this._hasMouseScrolled) {
            this._hasMouseScrolled = false;
            return true;
        }else {
            return false;
        }
    }

    // INITIALIZATION ---------------------------------------------------------------------------------------------------------
    constructor() {
        if (Input._instance) { throw new Error("Input singleton already initialized."); }

        this._timeKeyPressed = this._nt = this._ot = Date.now();
        this._dt = 0;
        this.mousePos = new Vector2D();

        // init ascii array
        this._ascii = new Array(222);
        this.fillAscii();

        // init key state array
        this._keyState = new Map<string, number>();
        this._keyArr = [];

        for (let i: number = 0; i < 222; i++) {
            if (this._ascii[i] !== undefined) {
                let asciiKey: string = this._ascii[i];

                // fill keyArr with all available keys define in _ascii
                this._keyArr.push(asciiKey);

                // set keyState to default 0
                this._keyState.set(asciiKey, 0);
            }
        }
    }

    /**
     * Must be called in order for the class to work
     * @param stage the stage that all of the listeners will be attached to
     *
     */
    public activate(stage: Stage): void {
        this._stage = stage;

        // add key listeners
        stage.addEventListener(KeyboardEvent.KEY_DOWN, this.bindFunction(this.keyPress));  // FIXME: @HTML5 - useWeakReference was true
        stage.addEventListener(KeyboardEvent.KEY_UP, this.bindFunction(this.keyRelease));  // FIXME: @HTML5 - useWeakReference was true

        // mouse listeners
        stage.addEventListener("mousedown", this.bindFunction(this.mousePress));           // FIXME: @HTML5 - useWeakReference was true
        stage.addEventListener("pressup", this.bindFunction(this.mouseRelease));           // FIXME: @HTML5 - useWeakReference was true
        stage.addEventListener("mousemove", this.bindFunction(this.mouseMove));            // FIXME: @HTML5 - useWeakReference was true
        stage.addEventListener("mousewheel", this.bindFunction(this.mouseScroll));         // FIXME: @HTML5 - useWeakReference was true

    }

    // ======================
    // update
    // ======================
    public update(): void {
        this._nt = Date.now();
        this._dt = this._nt - this._ot;
        this._ot = this._nt;

        // update used keys, set used keys to a default state
        for (let i: number = 0; i < this._keyArr.length; i++) {
            if (this._keyState.get(this._keyArr[i]) !== 0) {
                this._keyState.set(this._keyArr[i], 0);
            }
        }

        // end mouse release
        this.isMouseReleased = false;

    }

    // ======================
    // isKeyDown
    // ======================
    public isKeyDown(key: string): boolean {
        return (this._keyState.get(key) > 0);
    }

    // ======================
    //  isKeyPressed
    // ======================
    public isKeyPressed(key: string): boolean {
        return this._keyState.get(key) === 1;
    }

    // ======================
    //  isKeyReleased
    // ======================
    public isKeyReleased(key: string): boolean {
        return (this._keyState.get(key) === -1);
    }

    // ======================
    // set up ascii text
    // ======================
    private fillAscii(): void {
        this._ascii[65] = "A";
        this._ascii[66] = "B";
        this._ascii[67] = "C";
        this._ascii[68] = "D";
        this._ascii[69] = "E";
        this._ascii[70] = "F";
        this._ascii[71] = "G";
        this._ascii[72] = "H";
        this._ascii[73] = "I";
        this._ascii[74] = "J";
        this._ascii[75] = "K";
        this._ascii[76] = "L";
        this._ascii[77] = "M";
        this._ascii[78] = "N";
        this._ascii[79] = "O";
        this._ascii[80] = "P";
        this._ascii[81] = "Q";
        this._ascii[82] = "R";
        this._ascii[83] = "S";
        this._ascii[84] = "T";
        this._ascii[85] = "U";
        this._ascii[86] = "V";
        this._ascii[87] = "W";
        this._ascii[88] = "X";
        this._ascii[89] = "Y";
        this._ascii[90] = "Z";
        this._ascii[48] = "0";
        this._ascii[49] = "1";
        this._ascii[50] = "2";
        this._ascii[51] = "3";
        this._ascii[52] = "4";
        this._ascii[53] = "5";
        this._ascii[54] = "6";
        this._ascii[55] = "7";
        this._ascii[56] = "8";
        this._ascii[57] = "9";
        this._ascii[32] = "Space";
        this._ascii[13] = "Enter";
        this._ascii[17] = "Ctrl";
        this._ascii[16] = "Shift";
        this._ascii[192] = "~";
        this._ascii[38] = "Up";
        this._ascii[40] = "Down";
        this._ascii[37] = "Left";
        this._ascii[39] = "Right";
        this._ascii[96] = "Numpad 0";
        this._ascii[97] = "Numpad 1";
        this._ascii[98] = "Numpad 2";
        this._ascii[99] = "Numpad 3";
        this._ascii[100] = "Numpad 4";
        this._ascii[101] = "Numpad 5";
        this._ascii[102] = "Numpad 6";
        this._ascii[103] = "Numpad 7";
        this._ascii[104] = "Numpad 8";
        this._ascii[105] = "Numpad 9";
        this._ascii[111] = "Numpad /";
        this._ascii[106] = "Numpad *";
        this._ascii[109] = "Numpad -";
        this._ascii[107] = "Numpad +";
        this._ascii[110] = "Numpad .";
        this._ascii[45] = "Insert";
        this._ascii[46] = "Delete";
        this._ascii[33] = "Page Up";
        this._ascii[34] = "Page Down";
        this._ascii[35] = "End";
        this._ascii[36] = "Home";
        this._ascii[112] = "F1";
        this._ascii[113] = "F2";
        this._ascii[114] = "F3";
        this._ascii[115] = "F4";
        this._ascii[116] = "F5";
        this._ascii[117] = "F6";
        this._ascii[118] = "F7";
        this._ascii[119] = "F8";
        this._ascii[188] = ",";
        this._ascii[190] = ".";
        this._ascii[186] = ";";
        this._ascii[222] = "'";
        this._ascii[219] = "[";
        this._ascii[221] = "]";
        this._ascii[189] = "-";
        this._ascii[187] = "+";
        this._ascii[220] = "\\";
        this._ascii[191] = "/";
        this._ascii[9] = "TAB";
        this._ascii[8] = "Backspace";
        this._ascii[27] = "ESC";
    }

    // ======================
    // mousePress listener
    // ======================
    private mousePress(e: MouseEvent): void {
        this.isMouseDown = true;
        this._timeMousePressed = Date.now();
    }

    // ======================
    // mousePress listener
    // ======================
    private mouseRelease(e: MouseEvent): void {
        this.isMouseDown = false;
        this.isMouseReleased = true;
        this._timeMouseReleased = Date.now();
    }

    // ======================
    // mouseMove listener
    // ======================
    private mouseMove(e: MouseEvent): void {
        this.mouseX = e.stageX - this._stage.x;
        this.mouseY = e.stageY - this._stage.y;
        this.mousePos.x = this.mouseX;
        this.mousePos.y = this.mouseY;
    }

    // ======================
    // mouseScroll function
    // ======================
    private mouseScroll(e: MouseEvent): void {
        this._hasMouseScrolled = true;
        this.mouseDelta = e.delta;
    }

    // ======================
    // keyPress function
    // ======================
    private keyPress(e: KeyboardEvent): void {

        // trace ( e.which + " : " + ascii[e.which] );

        // set keyState
        this._keyState.set(e.key, 1);

        // last key (for key config)
        this.lastKeyName = e.key;

        if (this._keyState.get(e.key) === 1) {
            this._timeKeyPressed = Date.now();
        }
    }

    // ======================
    // keyRelease function
    // ======================
    private keyRelease(e: KeyboardEvent): void {
        this._keyState.set(e.key, -1);
        this._timeKeyReleased = Date.now();
    }
}
